import * as sessionReplay from '@amplitude/session-replay-browser';
import type { AnalyticsBrowser } from '@segment/analytics-next';

import Cookies from 'js-cookie';

import { anonymousId } from './data';

const AMPLITUDE_API_KEY = process.env.REACT_APP_AMPLITUDE_BROWSER_KEY;

let isSessionReplayInitialized = false;

/**
 * Helper methods to get and set tracking cookies
 * These cookies are read by both the frontend and backend for consistent tracking
 */

const getStoredSessionId = (): number => {
  // Try backend cookie first, fall back to legacy cookie
  const backendCookie = Cookies.get('thm-amplitude-session-id');
  const legacyCookie = Cookies.get('analytics_session_id');
  const cookieValue = backendCookie || legacyCookie;
  return cookieValue ? Number(cookieValue) : 0;
};

const setStoredSessionId = (sessionId: number): void => {
  const cookieOptions = { expires: 400 }; // 400 days to match thm-aid cookie
  // Set cookie that backend expects
  Cookies.set('thm-amplitude-session-id', String(sessionId), cookieOptions);
  // Keep legacy cookie for backward compatibility
  Cookies.set('analytics_session_id', String(sessionId), cookieOptions);
  localStorage.setItem('analytics_session_id', String(sessionId));
};

const setDeviceIdCookie = (deviceId: string): void => {
  Cookies.set('thm-amplitude-device-id', deviceId, { expires: 400 });
};

const setSessionReplayIdCookie = (sessionReplayId: string): void => {
  Cookies.set('thm-session-replay', sessionReplayId, { expires: 400 });
};

// Check if we should use proxy (same logic as current amplitude.ts)
const checkShouldUseProxy = async (): Promise<boolean> => {
  try {
    const amplitudeResponse = await fetch('https://cdn.amplitude.com/libs/analytics-browser-2.11.9-min.js.gz');
    return !amplitudeResponse.ok;
  } catch {
    return true;
  }
};

export const initSessionReplay = async (analyticsInstance: AnalyticsBrowser) => {
  if (!AMPLITUDE_API_KEY || !analyticsInstance) {
    console.warn('Session replay initialization skipped: Missing API keys');
    return;
  }

  try {
    // Get user info from Segment
    const user = await analyticsInstance.user();
    const storedSessionId = getStoredSessionId();
    const deviceId = user.anonymousId() || anonymousId;

    // Check if we should use proxy
    const shouldUseProxy = await checkShouldUseProxy();

    await sessionReplay.init(AMPLITUDE_API_KEY, {
      sessionId: storedSessionId,
      deviceId,
      ...(shouldUseProxy && { serverUrl: 'https://insights-proxy-worker.tryhackme.com/api' }),
    }).promise;

    isSessionReplayInitialized = true;

    setDeviceIdCookie(deviceId);
    if (storedSessionId > 0) {
      setStoredSessionId(storedSessionId);
    }

    const initialProperties = sessionReplay.getSessionReplayProperties();
    const initialReplayId = initialProperties['[Amplitude] Session Replay ID'];
    if (initialReplayId && typeof initialReplayId === 'string') {
      setSessionReplayIdCookie(initialReplayId);
    }

    // Single middleware to handle both session ID monitoring and session replay properties
    analyticsInstance.addSourceMiddleware(({ payload, next }) => {
      // Monitor and sync session ID changes from Segment's Amplitude Actions integration
      const actionsAmplitude = payload.obj?.integrations?.['Actions Amplitude'];

      if (actionsAmplitude && typeof actionsAmplitude === 'object' && !Array.isArray(actionsAmplitude)) {
        const nextSessionId = (actionsAmplitude as Record<string, unknown>).session_id;

        if (nextSessionId && typeof nextSessionId === 'number') {
          const currentSessionId = getStoredSessionId();

          if (currentSessionId < nextSessionId) {
            setStoredSessionId(nextSessionId);
            sessionReplay.setSessionId(nextSessionId);

            if (process.env.NODE_ENV === 'development') {
              console.log('[Session Replay] Session ID updated:', nextSessionId);
            }
          }
        }
      }

      // Attach session replay properties to track and page events
      const eventType = payload.type();

      if (eventType === 'track' || eventType === 'page') {
        const sessionReplayProperties = sessionReplay.getSessionReplayProperties();

        if (Object.keys(sessionReplayProperties).length > 0) {
          payload.obj.properties = {
            ...payload.obj.properties,
            ...sessionReplayProperties,
          };

          // Force Session Replay's device_id into Amplitude Actions integration

          if (sessionReplayProperties.device_id) {
            payload.obj.integrations = {
              ...payload.obj.integrations,
              'Actions Amplitude': {
                ...(typeof payload.obj.integrations?.['Actions Amplitude'] === 'object' &&
                !Array.isArray(payload.obj.integrations['Actions Amplitude'])
                  ? payload.obj.integrations['Actions Amplitude']
                  : {}),
                device_id: sessionReplayProperties.device_id,
              },
            };
          }

          // Keep session replay ID cookie in sync for backend tracking
          const sessionReplayId = sessionReplayProperties['[Amplitude] Session Replay ID'];
          if (sessionReplayId && typeof sessionReplayId === 'string') {
            setSessionReplayIdCookie(sessionReplayId);
          }

          if (process.env.NODE_ENV === 'development' && sessionReplayId) {
            console.log('[Session Replay] Properties attached to event:', sessionReplayProperties);
          }
        }
      }

      next(payload);
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('[Session Replay] Initialized successfully');
      console.log('[Session Replay] Device ID:', deviceId);
      console.log('[Session Replay] Initial Session ID:', storedSessionId);
    }
  } catch (error) {
    console.error('Failed to initialize session replay:', error);
    if (process.env.NODE_ENV === 'development') {
      throw error;
    }
  }
};

/**
 * Returns the current session replay properties from Amplitude's Session Replay SDK.
 * These properties should be included with every event to link events to session replays.
 *
 * According to Amplitude docs: https://amplitude.com/docs/session-replay/session-replay-standalone-sdk
 * The SDK returns properties with the correct naming that should be spread directly into events:
 * - `[Amplitude] Session Replay ID`: Links the event to the session replay
 * - `session_id`: Amplitude's session ID (numeric timestamp) for proper session grouping
 * - `device_id`: Device identifier
 *
 */
export const getSessionReplayProperties = (): Record<string, string | number | boolean | null> => {
  if (!isSessionReplayInitialized) {
    return {};
  }

  try {
    return sessionReplay.getSessionReplayProperties();
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Session Replay] Failed to get properties:', error);
    }
    return {};
  }
};
